import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowUpRight, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import TextareaAutosize from 'react-textarea-autosize'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import type { ChecklistItemProps } from '../type'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { useChecklistItemMutations } from '@/lib/db/rxdb/hooks'
import { cn } from '@/lib/utils'
import { createDragExtractor } from '@/lib/utils/dragDrop'


export type ChecklistItemReorderEvent = {
  sourceId: string
  targetId: string
  dropPosition: 'before' | 'after'
}

const extractChecklistItemDragData = createDragExtractor('checklist-item')

export function ChecklistItem({
  item,
  draggingId,
  onDragStart,
  onDragEnd,
  onReorder,
  dropIndicator,
  onDropIndicatorChange,
  onMakeSubTask,
}: ChecklistItemProps) {
  const { t } = useTranslation('tasks')
  const { updateChecklistItem, deleteChecklistItem } =
    useChecklistItemMutations()
  const [isEditing, setIsEditing] = useState(false)
  const [content, setContent] = useState(item.content || '')
  const [isHovered, setIsHovered] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setContent(item.content || '')
  }, [item.content])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleCheckToggle = useCallback(
    async (checked: boolean) => {
      try {
        await updateChecklistItem(item.id, { isCompleted: checked })
      } catch (error) {
        // Error handling
      }
    },
    [item.id, updateChecklistItem],
  )

  const handleContentSave = useCallback(async () => {
    const trimmed = content.trim()

    if (trimmed.length === 0) {
      setContent(item.content || '')
      setIsEditing(false)
      return
    }

    if (trimmed === item.content) {
      setIsEditing(false)
      return
    }

    try {
      await updateChecklistItem(item.id, { content: trimmed })
      setContent(trimmed)
      setIsEditing(false)
    } catch (error) {
      // Error handling
      setContent(item.content || '')
      setIsEditing(false)
    }
  }, [content, item.id, item.content, updateChecklistItem])

  const handleDelete = useCallback(async () => {
    try {
      await deleteChecklistItem(item.id)
    } catch (error) {
      // Error handling
    }
  }, [item.id, deleteChecklistItem])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        setContent(item.content || '')
        setIsEditing(false)
      }
    },
    [item.content],
  )

  // Set up drag and drop — the whole row is the handle
  useEffect(() => {
    const element = itemRef.current
    if (!element) {
      return
    }

    if (!onReorder) {
      return
    }

    return combine(
      draggable({
        element,
        // Dragging while editing would steal text selection in the textarea
        canDrag: () => !isEditing,
        getInitialData: () => ({ type: 'checklist-item', id: item.id }),
        onDragStart: () => {
          onDragStart?.(item.id)
          onDropIndicatorChange?.(null)
        },
        onDrop: () => {
          onDragEnd?.()
          onDropIndicatorChange?.(null)
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) =>
          Boolean(extractChecklistItemDragData(source.data)),
        getData: () => ({ type: 'checklist-item', id: item.id }),
        onDropTargetChange: ({ location, source, self }) => {
          const itemData = extractChecklistItemDragData(source.data)
          if (!itemData || itemData.id === item.id) {
            if (onDropIndicatorChange && dropIndicator?.id === item.id) {
              onDropIndicatorChange(null)
            }
            return
          }

          const rect = (self.element as HTMLElement).getBoundingClientRect()
          const pointerY = location.current.input.clientY
          const shouldPlaceAfter = pointerY > rect.top + rect.height / 2

          onDropIndicatorChange?.({
            id: item.id,
            position: shouldPlaceAfter ? 'after' : 'before',
          })
        },
        onDragLeave: () => {
          if (onDropIndicatorChange && dropIndicator?.id === item.id) {
            onDropIndicatorChange(null)
          }
        },
        onDrop: ({ location, source, self }) => {
          const itemData = extractChecklistItemDragData(source.data)
          onDropIndicatorChange?.(null)
          if (!itemData || itemData.id === item.id) {
            return
          }

          const innermostTarget = location.current.dropTargets[0]
          if (innermostTarget?.element !== self.element) {
            return
          }

          const rect = (self.element as HTMLElement).getBoundingClientRect()
          const pointerY = location.current.input.clientY
          const shouldPlaceAfter = pointerY > rect.top + rect.height / 2

          onReorder({
            sourceId: itemData.id,
            targetId: item.id,
            dropPosition: shouldPlaceAfter ? 'after' : 'before',
          })
        },
      }),
    )
  }, [
    item.id,
    isEditing,
    onReorder,
    onDragStart,
    onDragEnd,
    onDropIndicatorChange,
    dropIndicator?.id,
  ])

  const isDragging = draggingId === item.id
  const showIndicatorBefore =
    dropIndicator?.id === item.id && dropIndicator?.position === 'before'
  const showIndicatorAfter =
    dropIndicator?.id === item.id && dropIndicator?.position === 'after'

  return (
    <div
      ref={itemRef}
      className={cn(
        'group relative flex items-start gap-2 rounded-md px-2 py-1.5 hover:bg-secondary transition-all',
        isDragging && 'opacity-40',
        (showIndicatorBefore || showIndicatorAfter) && 'bg-primary/5',
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {showIndicatorBefore && (
        <div className="pointer-events-none absolute left-0 right-0 -top-1 h-2 flex items-center px-2">
          <div className="w-full h-0.5 bg-primary rounded-full shadow-sm" />
        </div>
      )}
      <Checkbox
        checked={item.isCompleted}
        onCheckedChange={(checked) => void handleCheckToggle(checked === true)}
        className="mt-1 shrink-0 rounded-[4px] data-[state=checked]:border-foreground data-[state=checked]:bg-foreground data-[state=checked]:text-background"
      />

      {isEditing ? (
        <TextareaAutosize
          ref={inputRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onBlur={() => void handleContentSave()}
          onKeyDown={handleKeyDown}
          minRows={1}
          className="flex-1 resize-none rounded bg-card px-2 py-1 text-base leading-snug text-foreground focus:outline-none focus:ring-0 md:text-sm"
        />
      ) : (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className={cn(
            'flex-1 min-w-0 pr-8 text-left text-sm leading-snug text-foreground outline-none break-words whitespace-pre-wrap',
            item.isCompleted && 'text-muted-foreground/70 line-through',
          )}
        >
          {item.content}
        </button>
      )}

      {!isEditing && (
        <div
          className={cn(
            'absolute right-2 top-1 flex items-center gap-0.5 rounded-md bg-secondary pl-1 transition-opacity',
            isHovered ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          {onMakeSubTask && !item.isCompleted && (
            <button
              type="button"
              onClick={() => {
                void onMakeSubTask(item.content || '').then((created) => {
                  if (created) void deleteChecklistItem(item.id)
                })
              }}
              className="inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[12px] text-muted-foreground hover:bg-background hover:text-foreground"
            >
              <ArrowUpRight className="size-3.5" />
              {t('subTask.make')}
            </button>
          )}
          <Button type="button" variant="ghost" size="icon-sm" className="size-6" onClick={() => void handleDelete()} aria-label={t('rowKeys.delete')}>
            <X className="size-3.5 text-muted-foreground/70" />
          </Button>
        </div>
      )}
      {showIndicatorAfter && (
        <div className="pointer-events-none absolute left-0 right-0 -bottom-1 h-2 flex items-center px-2">
          <div className="w-full h-0.5 bg-primary rounded-full shadow-sm" />
        </div>
      )}
    </div>
  )
}
